// Integrytas Group - Common Security Module (CSM)
// The single page personalization control: one button (the entry point for grid
// columns AND the FieldRenderer form design panel) plus, while personalizing, a
// tier selector that chooses where changes save (System / Role / User).
//
// In the real shell this renders once, in the title bar (variant="header"), bound
// to the active page via the chrome bridge. The per-page/per-modal <PersonalizationToggle/>
// calls suppress to null there (SuppressInlineToggleContext) so there is no double
// button; rendered standalone (the §17 test) they draw inline and the gate passes.
//
// Gating: the button is ALWAYS clickable — if the user has no usable tier, or the
// only avenue is a Role tier another role owns, clicking explains why instead of
// personalizing.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { usePersonalization } from '../../lib/use_personalization';
import { useSuppressInlineToggle } from '../../lib/suppress_inline_toggle';
import { isDesignDirty, setDesignDirty } from '../../lib/design_dirty';
import { ConfirmModal } from '../common/ConfirmModal';
import type { UldTier } from '../../hooks/use_uld_layout';

const TIER_LABEL: Record<UldTier, string> = { SYSTEM: 'System', ROLE: 'Role', USER: 'User' };

interface Props {
  /** 'header' styles the button white-on-blue for the app title bar. */
  variant?: 'toolbar' | 'header';
}

export function PersonalizationToggle({ variant = 'toolbar' }: Props) {
  const { t } = useTranslation();
  const suppressed = useSuppressInlineToggle();
  const {
    session, isPersonalizing, togglePersonalizing,
    availableTiers, tiersLoading, editTier, setEditTier, roleOwner,
    lastError, clearError, previewBaseline, startPreview,
  } = usePersonalization();
  const [confirmExit, setConfirmExit] = useState(false);
  const [blockedMsg, setBlockedMsg]   = useState<string | null>(null);
  const header = variant === 'header';

  const isAdmin = availableTiers.includes('SYSTEM'); // System tier ⇒ admin
  const roleOwnedByOther = !!roleOwner && !session.effective_user.roles.some(r => r.role_id === roleOwner.role_id);

  // Don't let a non-owner edit the Role tier — its save would be rejected
  // (exclusivity), so steer them away up front with the owner's name.
  const chooseTier = (tier: UldTier) => {
    if (tier === 'ROLE' && roleOwnedByOther && !isAdmin) {
      setBlockedMsg(t('common.personalization.role_owned_by_other', `This page's Role design is owned by ${roleOwner?.display_text ?? 'another role'} — your role can't edit it. Choose System or User instead.`));
      return;
    }
    setEditTier(tier);
  };
  // Blocked when the user's only avenue is the Role tier and another role owns it.
  const blockedByRole = availableTiers.length > 0 && availableTiers.every(t => t === 'ROLE') && roleOwnedByOther;
  // No usable tier at all (rights). The button stays clickable and explains why.
  const noRights = !tiersLoading && availableTiers.length === 0;

  const requestToggle = () => {
    if (isPersonalizing) {
      if (isDesignDirty()) { setConfirmExit(true); return; }
      togglePersonalizing();
      return;
    }
    if (noRights) {
      setBlockedMsg(t('common.personalization.no_rights', "You don't have personalization rights on this page."));
      return;
    }
    if (blockedByRole) {
      setBlockedMsg(t('common.personalization.design_owned_by_other', `This page's design is owned by ${roleOwner?.display_text ?? 'another role'} and can't be personalized by your role.`));
      return;
    }
    togglePersonalizing();
  };

  const exitNow = () => { setConfirmExit(false); setDesignDirty(false); togglePersonalizing(); };

  const title = isPersonalizing
    ? t('common.personalization.exit', 'Exit personalization')
    : t('common.personalization.personalize_screen', 'Personalize this screen');

  const originalTitle = editTier === 'SYSTEM'
    ? t('common.personalization.show_original_system', 'Show the page with no personalization (system default)')
    : t('common.personalization.show_original_tier', `Show the page without ${TIER_LABEL[editTier]}-level personalization`);

  // Inline copies suppress inside the real shell (the header instance drives it).
  if (suppressed) return null;

  const buttonClass = header
    ? 'flex h-7 w-7 items-center justify-center rounded ' +
      (previewBaseline
        ? 'cursor-not-allowed text-white/30'
        : isPersonalizing ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white')
    : 'flex h-7 w-7 items-center justify-center rounded border transition-colors ' +
      (isPersonalizing
        ? 'border-primary bg-primary text-white'
        : 'border-csm-border bg-white text-csm-muted hover:border-primary hover:text-primary');

  return (
    <>
      <div className="flex items-center gap-1.5">
        {/* Tier selector — visible while personalizing so the user always sees
            (and can choose) whether changes apply to System / Role / User. */}
        {isPersonalizing && availableTiers.length > 0 && (
          <span className={'flex items-center gap-1 text-csm-2xs ' + (header ? 'text-white/80' : 'text-csm-muted')}>
            <span className="uppercase tracking-wide">{t('common.personalization.saving_to', 'Saving to')}</span>
            {availableTiers.length > 1 ? (
              <select
                value={editTier}
                onChange={e => chooseTier(e.target.value as UldTier)}
                title={t('common.personalization.choose_tier', 'Choose which tier your changes are saved to')}
                className="rounded border border-csm-border bg-white px-1 py-0.5 text-csm-2xs text-csm-ink"
              >
                {availableTiers.map(t => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
              </select>
            ) : (
              <span className="rounded bg-csm-surface-alt px-1.5 py-0.5 font-semibold text-csm-ink">{TIER_LABEL[availableTiers[0]]}</span>
            )}
            {editTier === 'ROLE' && roleOwner && (
              <span className={header ? 'text-white/60' : 'text-csm-watermark'}>· {roleOwner.display_text}</span>
            )}
          </span>
        )}

        {/* Show original — visible only while actively personalizing. */}
        {isPersonalizing && (
          <button
            type="button"
            data-testid="personalization-show-original"
            onClick={startPreview}
            title={originalTitle}
            className={header
              ? 'flex h-7 items-center rounded border border-white/25 bg-white/5 px-2 text-csm-2xs font-medium text-white/80 hover:bg-white/10 hover:text-white'
              : 'flex h-7 items-center rounded border border-csm-border bg-white px-2 text-csm-2xs font-medium text-csm-muted hover:border-primary hover:text-primary'}
          >
            {t('common.personalization.show_original', 'Show original')}
          </button>
        )}

        <button
          type="button"
          data-testid="personalization-toggle"
          disabled={previewBaseline}
          onClick={requestToggle}
          title={title}
          className={buttonClass}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </div>

      {confirmExit && (
        <ConfirmModal
          title={t('common.personalization.unsaved_title', 'Unsaved design changes')}
          message={t('common.personalization.unsaved_message', 'You have unsaved layout changes. Discard them and exit personalization?')}
          confirmLabel={t('common.personalization.discard_exit', 'Discard & exit')}
          cancelLabel={t('common.personalization.keep_editing', 'Keep editing')}
          danger
          onConfirm={exitNow}
          onCancel={() => setConfirmExit(false)}
        />
      )}
      {blockedMsg && (
        <ConfirmModal
          title={t('common.personalization.locked_title', 'Personalization locked')}
          message={blockedMsg}
          confirmLabel={t('common.ok', 'OK')}
          cancelLabel={t('common.close', 'Close')}
          danger={false}
          onConfirm={() => setBlockedMsg(null)}
          onCancel={() => setBlockedMsg(null)}
        />
      )}
      {lastError && (
        <ConfirmModal
          title={t('common.personalization.change_not_saved', 'Change not saved')}
          message={lastError}
          confirmLabel={t('common.ok', 'OK')}
          cancelLabel={t('common.close', 'Close')}
          danger={false}
          onConfirm={clearError}
          onCancel={clearError}
        />
      )}
    </>
  );
}
